import React, { useState } from "react";
import { Loader2, Send, FileCheck2, Copy, Search } from "lucide-react";
import { toast } from "sonner";
import { Link, useSearchParams } from "react-router-dom";
import { useCategories } from "../hooks/use-categories";
import { submitTopic } from "../lib/api";
import { ContactAdminPanel } from "../components/ContactAdminPanel";

const Field = ({ label, children }) => (
  <div>
    <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39E93] block mb-2">
      // {label}
    </label>
    {children}
  </div>
);

const inputCls =
  "w-full bg-[#0F0F0F] border border-[#2D2A26] focus:border-[#D92D20] outline-none px-3 py-3 text-[#E4DFD3] placeholder:text-[#757065]";

const initialForm = () => ({
  title: "", category: "Politics", rationale: "", submitter: "",
});

// Surface user-facing validation that mirrors the server-side check so
// users don't burn a rate-limit slot on something the server will reject.
const validate = (form) => {
  const title = form.title.trim();
  const rationale = form.rationale.trim();
  if (title.length < 8) return "Title must be at least 8 characters.";
  if (title.length > 200) return "Title must be 200 characters or fewer.";
  if (rationale.length < 40) return "Please describe the controversy in at least 40 characters.";
  if (rationale.length > 3000) return "Description must be 3000 characters or fewer.";
  return null;
};

const SuccessCard = ({ submission, onReset }) => {
  const isRejected = submission.status === "REJECTED";
  const copy = async () => {
    try { await navigator.clipboard.writeText(submission.id); toast.success("Tracking ID copied"); } catch { /* noop */ }
  };
  return (
    <div className="border border-[#2D2A26] bg-[#131313] p-8 relative" data-testid="submission-success">
      <div className="absolute top-3 right-3 stamp">{isRejected ? "AUTO-REJECTED" : "RECEIVED"}</div>
      <div className="flex items-center gap-3 mb-5">
        <FileCheck2 size={22} className={isRejected ? "text-[#D92D20]" : "text-[#7ad08a]"} />
        <h2 className="font-display text-3xl text-[#E4DFD3]">
          {isRejected ? "Submission filtered out" : "Your file has been received"}
        </h2>
      </div>
      {isRejected ? (
        <p className="text-[#A39E93] mb-5">
          {submission.rejection_reason || "Our automated spam filter rejected this submission."}
        </p>
      ) : (
        <p className="text-[#A39E93] mb-5">
          It's now in the editorial queue. An AI screen runs first; if it looks like a genuine
          controversy, a human editor reviews it before anything goes public. You can check status
          any time using your tracking ID.
        </p>
      )}
      <div className="border border-[#2D2A26] bg-[#0a0a0a] p-4 mb-5">
        <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065] mb-2">// TRACKING ID</div>
        <div className="flex items-center justify-between gap-3">
          <code
            data-testid="submission-tracking-id"
            className="font-mono text-sm text-[#E4DFD3] break-all"
          >
            {submission.id}
          </code>
          <button
            type="button"
            onClick={copy}
            data-testid="copy-tracking-id-btn"
            className="flex items-center gap-1.5 border border-[#454038] hover:border-[#D92D20] text-[#A39E93] hover:text-[#E4DFD3] px-3 py-2 font-mono text-[9px] uppercase tracking-[0.25em]"
          >
            <Copy size={11} /> Copy
          </button>
        </div>
        <div className="font-mono text-[10px] text-[#757065] mt-3">
          Status: <span className="text-[#A39E93]">{submission.status}</span>
        </div>
      </div>
      <button
        type="button"
        onClick={onReset}
        data-testid="submit-another-btn"
        className="border border-[#454038] hover:border-[#D92D20] text-[#E4DFD3] px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em] mr-3"
      >
        File Another
      </button>
      <Link
        to={`/status/${submission.id}`}
        data-testid="check-status-link"
        className="inline-flex items-center gap-1.5 border border-[#454038] hover:border-[#D92D20] text-[#A39E93] hover:text-[#E4DFD3] px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em]"
      >
        <Search size={11} /> Check Status Later
      </Link>
    </div>
  );
};

const MODE_PRESETS = {
  correction: {
    eyebrow: "// CORRECTION INTAKE",
    stamp: "CORRECTION",
    title: "Submit a correction",
    intro:
      "Found a record on The Archive that contradicts its source — or a source that has been withdrawn? File the correction here. Editors review every flag.",
    titlePlaceholder: 'e.g. "Source no longer available for record X"',
    rationalePlaceholder:
      "Which record needs correcting? Link the source that contradicts it. The more specific, the faster we can act.",
    submitLabel: "Submit correction",
    rationaleHint: "Please include the URL or title of the record you are correcting.",
  },
  dispute: {
    eyebrow: "// DISPUTE / CLARIFICATION",
    stamp: "DISPUTE",
    title: "File a dispute or clarification",
    intro:
      "If you believe a record misrepresents you, your work, or a public document, file a dispute here. We do not practice guilt by association — clarifications and removals are part of the standard.",
    titlePlaceholder: 'e.g. "Clarification on record X"',
    rationalePlaceholder:
      "Briefly describe what is being misrepresented and link to the public document, filing, or statement that should be considered.",
    submitLabel: "Submit dispute",
    rationaleHint: "Link to the source or statement that should be on the record.",
  },
  default: {
    eyebrow: "// INTAKE FORM",
    stamp: "FORM-7B",
    title: "Submit a file",
    intro:
      "Found a controversy worth investigating? File it here. The archivist (human editor) reviews every submission before anything is published.",
    titlePlaceholder: 'e.g. "Boeing 737 MAX MCAS Concealment"',
    rationalePlaceholder:
      "Briefly justify why this belongs in the archive. Cite primary sources if you can…",
    submitLabel: "Submit for Editorial Review",
    rationaleHint: null,
  },
};

const Submit = () => {
  const { categories } = useCategories();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get("mode");
  const preset = MODE_PRESETS[mode] || MODE_PRESETS.default;
  const [form, setForm] = useState(initialForm());
  const [posting, setPosting] = useState(false);
  const [result, setResult] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate(form);
    if (err) { toast.error(err); return; }
    setPosting(true);
    try {
      // Prefix the title so editors can immediately see this is a correction
      // or dispute in the review queue, without needing a new endpoint.
      const titlePrefix = mode === "correction" ? "[CORRECTION] " : mode === "dispute" ? "[DISPUTE] " : "";
      const sub = await submitTopic({
        title: `${titlePrefix}${form.title.trim()}`,
        category: form.category,
        rationale: form.rationale.trim(),
        submitter: form.submitter.trim() || "anonymous",
      });
      setResult(sub);
      if (sub.status === "REJECTED") {
        toast.error("Filtered by spam checker");
      } else {
        toast.success("Submission received — under editorial review");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Submission failed";
      toast.error(typeof msg === "string" ? msg : "Submission failed");
    } finally {
      setPosting(false);
    }
  };

  const reset = () => { setForm(initialForm()); setResult(null); };

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-12 py-16" data-testid="submit-page">
      <div className="font-mono text-[10px] tracking-[0.3em] text-[#D92D20] mb-3">{preset.eyebrow}</div>
      <h1 className="font-display text-5xl md:text-6xl text-[#E4DFD3] mb-3" data-testid="submit-page-title">{preset.title}</h1>
      <p className="text-[#A39E93] max-w-2xl mb-8">{preset.intro}</p>

      {/* Route selector — evidence vs general contact */}
      <div className="flex flex-wrap items-stretch gap-3 mb-12" data-testid="intake-routes">
        <div className="flex-1 min-w-[240px] border border-[#2D2A26] bg-[#131313] px-4 py-3">
          <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#5DD4B3] mb-1">Evidence / leads / investigations</div>
          <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#E4DFD3]">→ File a submission below</div>
        </div>
        <div className="flex-1 min-w-[240px] border border-[#2D2A26] bg-[#131313] px-4 py-3 flex items-center justify-between gap-3">
          <div>
            <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#CBB892] mb-1">Questions / offers / general contact</div>
            <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#A39E93]">Reach the editorial desk directly</div>
          </div>
          <ContactAdminPanel />
        </div>
      </div>

      {result ? (
        <SuccessCard submission={result} onReset={reset} />
      ) : (
        <form onSubmit={handleSubmit} className="border border-[#2D2A26] bg-[#131313] p-8 space-y-6 relative">
          <div className="absolute top-3 right-3 stamp">{preset.stamp}</div>

          <Field label="TOPIC TITLE">
            <input
              value={form.title} onChange={set("title")}
              placeholder={preset.titlePlaceholder}
              data-testid="submit-title-input"
              maxLength={200}
              className={`${inputCls} font-heading text-lg`}
              required
            />
            <div className="font-mono text-[9px] text-[#757065] mt-1">{form.title.trim().length}/200 chars (min 8)</div>
          </Field>

          <Field label="CABINET">
            <select
              value={form.category} onChange={set("category")}
              data-testid="submit-category-select"
              className={`${inputCls} font-mono text-sm uppercase tracking-widest`}
            >
              {categories.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
            </select>
          </Field>

          <Field label={mode === "correction" || mode === "dispute" ? "WHAT NEEDS CHANGING?" : "WHY IS IT CONTROVERSIAL?"}>
            <textarea
              value={form.rationale} onChange={set("rationale")}
              rows={6} placeholder={preset.rationalePlaceholder}
              data-testid="submit-rationale-input"
              maxLength={3000}
              className={`${inputCls} font-body text-sm resize-none`}
              required
            />
            <div className="font-mono text-[9px] text-[#757065] mt-1">
              {form.rationale.trim().length}/3000 chars (min 40)
              {preset.rationaleHint && <span className="ml-2 text-[#CBB892]">· {preset.rationaleHint}</span>}
            </div>
          </Field>

          <Field label="YOUR CODENAME (OPTIONAL)">
            <input
              value={form.submitter} onChange={set("submitter")}
              placeholder="anonymous"
              data-testid="submit-codename-input"
              maxLength={60}
              className={`${inputCls} font-mono text-sm`}
            />
          </Field>

          <div className="pt-2 border-t border-[#2D2A26]">
            <button
              type="submit" disabled={posting}
              data-testid="submit-form-btn"
              className="w-full flex items-center justify-center gap-2 border border-[#454038] hover:border-[#D92D20] disabled:opacity-50 disabled:cursor-not-allowed text-[#E4DFD3] px-6 py-3 font-mono text-[10px] uppercase tracking-[0.25em]"
            >
              {posting
                ? <><Loader2 size={12} className="animate-spin" /> Sending…</>
                : <><Send size={12} /> {preset.submitLabel}</>}
            </button>
            <p className="font-mono text-[9px] text-[#757065] mt-3 text-center">
              All submissions pass an AI spam filter and a human editorial review before publication.
            </p>
          </div>
        </form>
      )}
    </div>
  );
};

export default Submit;
